import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ThumbsUp, Smile } from "lucide-react";

type ReactionType = 'like' | 'celebrate' | 'insightful' | 'love' | 'curious';

interface Reaction {
  id: number;
  newsId: number;
  userId: number;
  reactionType: ReactionType;
  user: {
    id: number;
    name: string;
  };
}

const REACTION_EMOJIS: Record<ReactionType, { emoji: string; label: string; color: string }> = {
  like: { emoji: '\u{1F44D}', label: 'Like', color: 'text-blue-500' },
  celebrate: { emoji: '\u{1F389}', label: 'Celebrate', color: 'text-yellow-500' },
  insightful: { emoji: '\u{1F4A1}', label: 'Insightful', color: 'text-amber-500' },
  love: { emoji: '\u2764\uFE0F', label: 'Love', color: 'text-red-500' },
  curious: { emoji: '\u{1F914}', label: 'Curious', color: 'text-purple-500' },
};

interface ReactionBarProps {
  newsId: number;
  initialReactionCounts?: Record<string, number>;
  compact?: boolean;
}

export function ReactionBar({ newsId, initialReactionCounts = {}, compact = false }: ReactionBarProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Fetch reactions for this news item
  const { data: reactions = [] } = useQuery<Reaction[]>({
    queryKey: [`/api/news/${newsId}/reactions`],
    staleTime: 30000,
  });

  // Calculate reaction counts
  const reactionCounts = reactions.reduce((acc, reaction) => {
    acc[reaction.reactionType] = (acc[reaction.reactionType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Get current user's reaction
  const userReaction = reactions.find(r => r.userId === user?.id);

  // Add reaction mutation with optimistic update
  const addReactionMutation = useMutation({
    mutationFn: async (reactionType: ReactionType) => {
      return await apiRequest("POST", `/api/news/${newsId}/reactions`, { reactionType });
    },
    onMutate: async (reactionType: ReactionType) => {
      await queryClient.cancelQueries({ queryKey: [`/api/news/${newsId}/reactions`] });
      const previous = queryClient.getQueryData<Reaction[]>([`/api/news/${newsId}/reactions`]);

      queryClient.setQueryData<Reaction[]>([`/api/news/${newsId}/reactions`], (old = []) => {
        const filtered = old.filter(r => r.userId !== user?.id);
        return [...filtered, {
          id: Date.now(),
          newsId,
          userId: user?.id || 0,
          reactionType,
          user: { id: user?.id || 0, name: user?.name || "" },
        }];
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([`/api/news/${newsId}/reactions`], context.previous);
      }
      toast({ title: "Error", description: "Could not add reaction.", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/news/${newsId}/reactions`] });
    },
  });

  // Remove reaction mutation with optimistic update
  const removeReactionMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("DELETE", `/api/news/${newsId}/reactions`);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [`/api/news/${newsId}/reactions`] });
      const previous = queryClient.getQueryData<Reaction[]>([`/api/news/${newsId}/reactions`]);

      queryClient.setQueryData<Reaction[]>([`/api/news/${newsId}/reactions`], (old = []) => {
        return old.filter(r => r.userId !== user?.id);
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([`/api/news/${newsId}/reactions`], context.previous);
      }
      toast({ title: "Error", description: "Could not remove reaction.", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/news/${newsId}/reactions`] });
    },
  });

  const handleReaction = (reactionType: ReactionType) => {
    if (!user) return;

    if (userReaction?.reactionType === reactionType) {
      // Remove reaction if same type clicked
      removeReactionMutation.mutate();
    } else {
      // Add or change reaction
      addReactionMutation.mutate(reactionType);
    }
    setIsPickerOpen(false);
  };

  const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + b, 0);

  // Get users who reacted for tooltip
  const getReactionUsers = (type: ReactionType) => {
    return reactions
      .filter(r => r.reactionType === type)
      .map(r => r.user.name)
      .slice(0, 5)
      .join(', ');
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "gap-1 h-8 px-2",
                userReaction && REACTION_EMOJIS[userReaction.reactionType as ReactionType].color
              )}
            >
              {userReaction ? (
                <span className="text-base">{REACTION_EMOJIS[userReaction.reactionType as ReactionType].emoji}</span>
              ) : (
                <ThumbsUp className="h-4 w-4" />
              )}
              {totalReactions > 0 && <span className="text-sm">{totalReactions}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" side="top">
            <div className="flex gap-1">
              {Object.entries(REACTION_EMOJIS).map(([type, { emoji, label }]) => (
                <TooltipProvider key={type}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleReaction(type as ReactionType)}
                        className={cn(
                          "text-2xl p-2 rounded-full hover:bg-muted transition-transform hover:scale-110",
                          userReaction?.reactionType === type && "bg-muted"
                        )}
                      >
                        {emoji}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{label}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Reaction summary */}
      {totalReactions > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="flex -space-x-1">
            {Object.entries(reactionCounts)
              .filter(([_, count]) => count > 0)
              .slice(0, 3)
              .map(([type]) => (
                <span key={type} className="text-base">
                  {REACTION_EMOJIS[type as ReactionType].emoji}
                </span>
              ))}
          </div>
          <span>{totalReactions} {totalReactions === 1 ? 'reaction' : 'reactions'}</span>
        </div>
      )}

      {/* Reaction buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={userReaction ? "secondary" : "outline"}
              size="sm"
              className="gap-2"
            >
              {userReaction ? (
                <>
                  <span className="text-base">{REACTION_EMOJIS[userReaction.reactionType as ReactionType].emoji}</span>
                  <span>{REACTION_EMOJIS[userReaction.reactionType as ReactionType].label}</span>
                </>
              ) : (
                <>
                  <Smile className="h-4 w-4" />
                  <span>React</span>
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" side="top">
            <div className="flex gap-1">
              {Object.entries(REACTION_EMOJIS).map(([type, { emoji, label }]) => (
                <TooltipProvider key={type}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleReaction(type as ReactionType)}
                        className={cn(
                          "text-2xl p-2 rounded-full hover:bg-muted transition-transform hover:scale-110",
                          userReaction?.reactionType === type && "bg-muted"
                        )}
                      >
                        {emoji}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{label}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Show reaction counts with tooltips */}
        {Object.entries(reactionCounts)
          .filter(([_, count]) => count > 0)
          .map(([type, count]) => (
            <TooltipProvider key={type}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 h-8 px-2"
                    onClick={() => handleReaction(type as ReactionType)}
                  >
                    <span className="text-base">{REACTION_EMOJIS[type as ReactionType].emoji}</span>
                    <span className="text-sm font-medium">{count}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{getReactionUsers(type as ReactionType)}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
      </div>
    </div>
  );
}
